import {
    AfterContentInit, ContentChild,
    Directive, OnDestroy, SkipSelf,
} from "@angular/core";
import { distinctUntilChanged, takeUntil } from "rxjs/operators";

import { includes } from "lodash";
import { Subscription } from "rxjs";
import { CommonFormManagerDirective } from "../../form-manager.directive";
import { ToggleFieldGroupComponent } from "../field-toggle-group.component";
import { NoneControlDirective } from "../none-control.directive";
import { ToggleControlComponent } from "../toggle-control.component";
import { FieldToggleManager } from "../toggle-manager.service";

@Directive({
    selector: "common-field-toggle-group[none-toggle-strategy]",
    providers: [FieldToggleManager],
})
export class NoneToggleStrategyDirective implements OnDestroy, AfterContentInit {
    @ContentChild(NoneControlDirective, { read: ToggleControlComponent, static: false })
    public noneControl: ToggleControlComponent;

    private state$: Subscription;
    private noneControlState$: Subscription;
    private shouldEvaluateNone = true;

    constructor(
        @SkipSelf() private formManager: CommonFormManagerDirective,
        private hostRef: ToggleFieldGroupComponent,
        private toggleManager: FieldToggleManager,
    ) { }

    public ngAfterContentInit(): void {
        this.toggleManager.state$.pipe(takeUntil(this.hostRef.toggleFieldGroupTouched$)).subscribe((state) => {
            this.handleNoneControl(true);
            this.setToggleFieldDisabledState(this.noneControl.toggleState);
        });

        this.hostRef.toggleFieldGroupTouched$.subscribe(() => {
            this.hostRef.toggleControls.forEach((toggleControl) => {
                toggleControl.toggleTouched = true;
            });
            this.state$ = this.toggleManager.state$.pipe(
                distinctUntilChanged(this.toggleManager.distinctUntilChangedFactory(this.allFieldProps)))
                .subscribe((state) => {
                    this.handleNoneControl();
                    this.setToggleFields();
                });

            this.noneControlState$ = this.noneControl.toggleState$.subscribe((state) => {
                this.hostRef.toggleFields.forEach((field) => field.isDisabled = state);
            });
        });
    }

    public ngOnDestroy(): void {
        if (this.state$) { this.state$.unsubscribe(); }
        if (this.noneControlState$) { this.noneControlState$.unsubscribe(); }
    }

    public setToggleFields(): void {
        if (!this.toggleManager.toggleFieldsAreEmpty) { this.setToggleFieldDisabledState(false); }
    }

    public setToggleFieldDisabledState(state): void {
        this.hostRef.toggleFields.forEach((toggleField) => toggleField.isDisabled = state);
    }

    public handleNoneControl(runOnce = false): void {
        const isOriginalVersion = this.formManager.stateProp === "originalFeatureProductVersions";
        const includesVarispeed = includes(this.allFieldProps, "varispeedType") ||
            includes(this.allFieldProps, "varispeedPercent");
        const needToBeunchecked = !isOriginalVersion && !this.noneControl.duplicate && includesVarispeed;
        if (this.shouldEvaluateNone || this.formManager.isEditForm) {
            if (includes(this.allFieldProps, "creditsFormat") ||
                includes(this.allFieldProps, "mpaaDisclaimerNumber") ||
                includes(this.allFieldProps, "wwodOpening") ||
                includes(this.allFieldProps, "reel2") ||
                includes(this.allFieldProps, "creditsRunTime") ||
                includes(this.allFieldProps, "mainOnEnds") ||
                includes(this.allFieldProps, "breakCount") ||
                includes(this.allFieldProps, "breakLength") ||
                includes(this.allFieldProps, "varispeedType") ||
                includes(this.allFieldProps, "varispeedPercent")) {
                if (this.dataIsEmpty && runOnce) {
                    this.noneControl.onToggleStateChange(true);
                }
                if (includesVarispeed) {
                    this.noneControl.onToggleStateChange(needToBeunchecked);
                }
            }
            this.shouldEvaluateNone = runOnce ? false : true;
        }

        if (this.dataIsPresent) {
            this.noneControl.onToggleStateChange(false);
            if (includes(this.allFieldProps, "creditsFormat") ||
                includes(this.allFieldProps, "varispeedType") ||
                includes(this.allFieldProps, "varispeedPercent")) {
                this.noneControl.disableToggle = false;
            } else {
                this.noneControl.disableToggle = true;
            }
        } else {
            this.noneControl.disableToggle = false;
        }
    }

    public get allFieldProps(): string[] {
        return this.toggleManager.allToggleFieldProps;
    }

    public get dataIsEmpty(): boolean {
        return this.toggleManager.allFieldsAreEmpty(this.allFieldProps);
    }

    public get dataIsPresent(): boolean {
        return !this.dataIsEmpty;
    }
}
